import React, { forwardRef, useCallback, useEffect, useRef } from 'react';

import { Button } from '../../components/Button/Button';
import { AthleteInfoPanel } from './AthleteInfoPanel';
import { ProductsPanel } from './ProductsPanel';
import type { Athlete } from './athletes';

type AthleteCardProps = {
    athlete: Athlete;
    isExpanded: boolean;
    onClick: () => void;
    // Off-viewport copies (for the infinite carousel): not focusable, not
    // announced, click handler ignored.
    hidden?: boolean;
    style?: React.CSSProperties;
    // Position in the carousel track, so the parent can address this card's
    // <video> (e.g. to carry playback position across the copy-snap).
    videoIndex?: number;
    // Mount the <video> at all. The parent only sets this for the expanded
    // card and the twin it is about to snap to; collapsed copies show the
    // <img> preview alone, so the track holds at most two media elements.
    mountVideo?: boolean;
    // Preload the full video instead of nothing. Same set as mountVideo here,
    // but kept separate so the two concerns can diverge later (e.g. mount a
    // neighbour but leave it preload="none").
    eagerVideo?: boolean;
    registerVideo?: (index: number, el: HTMLVideoElement | null) => void;
};

export const AthleteCard = forwardRef<HTMLDivElement, AthleteCardProps>(function AthleteCard(
    {
        athlete,
        isExpanded,
        onClick,
        hidden = false,
        style,
        videoIndex,
        mountVideo = false,
        eagerVideo = false,
        registerVideo,
    },
    ref,
) {
    const { firstName } = athlete;
    const interactive = !isExpanded && !hidden;

    const videoRef = useRef<HTMLVideoElement>(null);

    // Keep our own ref for play/pause and also hand the element to the parent
    // so it can seek this video during the copy-snap. Called with null on
    // unmount (including when mountVideo flips off), which clears the registry.
    const setVideoRef = useCallback((el: HTMLVideoElement | null) => {
        videoRef.current = el;
        if (videoIndex !== undefined) { registerVideo?.(videoIndex, el); }
    }, [registerVideo, videoIndex]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        if (isExpanded) {
            // currentTime may have been pre-seeked by the parent on a copy-snap,
            // so this resumes in place rather than restarting from frame 0.
            video.play().catch(() => {
                // play() can reject on its own (autoplay policy / interrupted
                // by a pause from a fast collapse); ignore.
            });
        } else {
            video.pause();
        }
    }, [isExpanded]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!interactive) { return; }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <div
            ref={ref}
            className={`athlete-card${isExpanded ? ' athlete-card--expanded' : ''}`}
            style={style}
            onClick={interactive ? onClick : undefined}
            onKeyDown={handleKeyDown}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : -1}
            aria-expanded={hidden ? undefined : isExpanded}
            aria-hidden={hidden || undefined}
            aria-label={`${athlete.name} card`}
        >
            <img
                className="athlete-card__preview"
                src={athlete.previewImage}
                alt=""
                aria-hidden="true"
            />
            {athlete.expandedVideo && mountVideo && (
                <video
                    ref={setVideoRef}
                    className="athlete-card__video"
                    src={athlete.expandedVideo}
                    muted
                    loop
                    playsInline
                    aria-hidden="true"
                    preload={eagerVideo ? 'auto' : 'none'}
                    poster={athlete.posterImage || undefined}
                />
            )}
            <div className="athlete-card__overlay" />

            {isExpanded ? (
                <div className="athlete-card__panel">
                    <AthleteInfoPanel athlete={athlete} />
                    <ProductsPanel athlete={athlete} />
                    <Button
                        label={`Shop ${firstName}'s faves`}
                        href={`/@${athlete.id}`}
                        className="athlete-card__cta"
                    />
                </div>
            ) : (
                <div className="athlete-card__name faves-h3">{athlete.name}</div>
            )}
        </div>
    );
});
